import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ContactsIcon from "@material-ui/icons/Contacts";
import FilterListIcon from "@material-ui/icons/FilterList";
import EmailIcon from "@material-ui/icons/Email";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";
import SortIcon from "@material-ui/icons/Sort";
import CloseIcon from "@material-ui/icons/Close";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import ContactsListPanel from "../ContactsListPanel";
import TicketsSearchResults from "../TicketsSearchResults";
import TabPanel from "../TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { useAttendanceWhatsappPresence } from "../../hooks/useContactWhatsappStatus";
import TicketsQueueSelect from "../TicketsQueueSelect";
import TicketsWhatsappSelect from "../TicketsWhatsappSelect";
import { Button } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { canShowMenu } from "../../helpers/menuAccess";
import { getManagedWhatsappIds, managedWhatsappsForUser } from "../../helpers/canAccessTicket";
import { isWhatsappDeleted } from "../../helpers/whatsappStatus";
import { toast } from "react-toastify";

// Pausa na digitação antes de disparar a busca (contatos, grupos e mensagens).
const SEARCH_DEBOUNCE_MS = 600;
// Teto de espera pelo resultado da sincronização manual de email (o lock da
// rodada por empresa no backend é de 180 s).
const EMAIL_SYNC_WATCHDOG_MS = 180000;

const useStyles = makeStyles(theme => ({
	ticketsWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		borderRadius:0,
	},

	tabsHeader: {
		flex: "none",
    display: "none",
		backgroundColor: theme.palette.tabHeaderBackground,
	},

	tabsInternal: {
		flex: "none",
		backgroundColor: theme.palette.tabHeaderBackground
	},

	settingsIcon: {
		alignSelf: "center",
		marginLeft: "auto",
		padding: 8,
	},

	tab: {
		minWidth: 120,
		width: 120,
	},

	internalTab: {
		minWidth: 120,
		width: 120,
		padding: 5
	},

	// As abas cedem 8px (44 -> 36) para caber mais um card de ticket na lista.
	compactTabs: {
		minHeight: 36,
		"& .MuiTabs-flexContainer": {
			minHeight: 36,
		},
		"& .MuiTab-root": {
			minHeight: 36,
			paddingTop: 0,
			paddingBottom: 0,
			// Padding lateral menor que o padrão do MUI (24px em sm+) e fonte reduzida
			// abrem folga para o rótulo + badge caberem na aba.
			paddingLeft: 6,
			paddingRight: 6,
			fontSize: "0.75rem",
		},
		"& .MuiTab-wrapper": {
			lineHeight: 1.1,
		},
	},

	// O badge continua colado no rótulo (canto superior direito do texto), mas sem
	// sobrar para fora: o espaço dele é reservado no fluxo por badgeSpaced, então o
	// conjunto texto + badge é centralizado como um bloco e o overflow: hidden da
	// aba não corta nada.
	badge: {
		"& .MuiBadge-badge": {
			top: -2,
			right: 0,
			transform: "none",
			transformOrigin: "unset",
			height: 16,
			minWidth: 16,
			padding: "0 4px",
			fontSize: "0.65rem",
			lineHeight: 1,
		},
		// O MUI esconde o badge vazio via transform: scale(0); como o transform foi
		// sobrescrito acima, esconder explicitamente (badge nulo durante a busca).
		"& .MuiBadge-invisible": {
			display: "none",
		},
	},

	// Só quando há contagem: reserva a largura do badge à direita do texto. Sem
	// contagem a classe não é aplicada e o rótulo fica perfeitamente centralizado.
	badgeSpaced: {
		paddingRight: 18,
	},

  tabAlarm: {
    backgroundColor: "#c62828",
    color: "#fff",
    "& .MuiBadge-badge": {
      backgroundColor: "#fff",
      color: "#c62828",
      fontWeight: 700,
    },
  },

  tabAlarmSelected: {
    backgroundColor: "#b71c1c",
    color: "#fff",
  },

	ticketOptionsBox: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		background: theme.palette.optionsBackground,
		padding: theme.spacing(1),
	},

	ticketSearchLine: {
		padding: theme.spacing(1),
	},

	serachInputWrapper: {
		flex: 1,
		background: theme.palette.total,
		display: "flex",
		borderRadius: 40,
		padding: 4,
		marginRight: theme.spacing(1),
	},

	searchIcon: {
		color: "grey",
		marginLeft: 6,
		marginRight: 6,
		alignSelf: "center",
	},

	searchInput: {
		flex: 1,
		border: "none",
		borderRadius: 30,
		// Esconde o X nativo do Chrome/Edge (type="search"); o clear fica só
		// no IconButton padrão ao lado.
		"& input::-webkit-search-cancel-button": {
			WebkitAppearance: "none",
			display: "none",
		},
	},

	insiderTabPanel: {
		height: '100%',
		marginTop: "-72px",
		paddingTop: "72px"
	},

	insiderDoubleTabPanel: {
		display:"flex",
		flexDirection: "column",
		marginTop: "-72px",
		paddingTop: "72px",
		height: "100%"
	},

	iconLabelWrapper: {
		flexDirection: "row",
		'& > *:first-child': {
			marginBottom: '3px !important',
			marginRight: 16
		}
	},
	insiderTabLabel: {
		[theme.breakpoints.down(1600)]: {
			display:'none'
		}
	},
	smallFormControl: {
		'& .MuiOutlinedInput-input': {
			padding: "12px 10px",
		},
		'& .MuiInputLabel-outlined': {
			marginTop: "-6px"
		}
	},

	unifiedSearchWrapper: {
		display: "flex",
		alignItems: "center",
		background: theme.palette.optionsBackground,
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		gap: 8,
	},

	searchInputContainer: {
		flex: 1,
		display: "flex",
		alignItems: "center",
		background: theme.palette.total,
		borderRadius: 20,
		padding: "1px 10px",
	},

	filterPopover: {
		padding: theme.spacing(2),
		minWidth: 280,
		maxWidth: 320,
	},

	filterSection: {
		marginBottom: theme.spacing(2),
		'&:last-child': {
			marginBottom: 0,
		}
	},

	filterLabel: {
		fontWeight: 500,
		marginBottom: theme.spacing(1),
		color: theme.palette.text.secondary,
		fontSize: 12,
		textTransform: "uppercase",
	},

	// Switch small: a bolinha fica a 4px da borda do componente; com o -11 padrão
	// do FormControlLabel ela invadia o padding do popover.
	filterSwitchLabel: {
		marginLeft: -4,
	},

	// Checkbox small: 9px de padding + ~2,5px de folga no ícone; -11 leva a borda
	// da caixa até a borda do popover, e o recuo de `sortOption` a alinha com o
	// texto das opções de ordenação.
	filterCheckboxLabel: {
		marginLeft: theme.spacing(1.5) - 11,
		marginRight: 0,
	},

	sortOption: {
		paddingLeft: theme.spacing(1.5),
		paddingRight: theme.spacing(1.5),
	},

	activeFilterBadge: {
		position: "absolute",
		top: 4,
		right: 4,
		width: 8,
		height: 8,
		borderRadius: "50%",
		background: theme.palette.primary.main,
	},
}));

const TicketsManagerTabs = ({ onSelectContact }) => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [searchBy, setSearchBy] = useState("all");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [syncingEmail, setSyncingEmail] = useState(false);
  // Timer de segurança da rodada manual de email. Enquanto existe, esta aba
  // está esperando o resultado pelo socket.
  const emailSyncWatchdogRef = useRef(null);
  const searchInputRef = useRef();
  const { user, menus } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  // Número online e "digitando..." nos cards enquanto esta tela está aberta.
  useAttendanceWhatsappPresence();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { profile } = user;

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  // Enquanto ha busca ativa, o badge mostra quantos resultados a aba trouxe.
  const [searchResultCount, setSearchResultCount] = useState(0);
  const [openAlarmCount, setOpenAlarmCount] = useState(0);
  const [pendingAlarmCount, setPendingAlarmCount] = useState(0);
  const [alarmEnabled, setAlarmEnabled] = useState(false);

  const userQueues = Array.isArray(user?.queues) ? user.queues : [];
  const userQueueIds = userQueues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  // Tags do CONTATO (ContactTags) — `selectedTags` acima são tags do ticket.
  const [selectedContactTags, setSelectedContactTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortKey, setSortKey] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [autoReorder, setAutoReorder] = useState(() => {
    const saved = localStorage.getItem("ticketsAutoReorder");
    if (saved === null) {
      localStorage.setItem("ticketsAutoReorder", "true");
      return true;
    }
    return saved === "true";
  });

  const filtersStorageKey = `ticketsFilters:${user?.companyId || "default"}:${user?.id}`;

  const normalizeIdList = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));
    }
    return [];
  };

  // Regra única em helpers/menuAccess: admin sempre liberado, não-admin só com a
  // chave marcada no perfil, lista vazia nega.
  const canAccessFilter = (key) => canShowMenu(user, menus, key);

  const canShowAllFilter = canAccessFilter("tickets_filter_show_all");
  const canShowQueueFilter = canAccessFilter("tickets_filter_queues");
  const canShowWhatsappFilter = canAccessFilter("tickets_filter_whatsapp");

  // O filtro só oferece as conexões que o usuário controla (padrão + atribuídas),
  // mesmo com o escopo por conexão desligado. Admin vê todas.
  // Conexão excluída nunca entra nas opções do filtro.
  const filterWhatsApps = useMemo(
    () => managedWhatsappsForUser(user, whatsApps).filter((w) => !isWhatsappDeleted(w)),
    [user, whatsApps]
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(filtersStorageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);

      if (typeof saved.showAllTickets === "boolean") {
        setShowAllTickets(saved.showAllTickets);
      }
      if (Array.isArray(saved.selectedQueueIds)) {
        // Filtrar apenas filas que o usuário tem acesso
        const userQueueIds = userQueues.map((q) => q.id);
        const validQueueIds = saved.selectedQueueIds.filter(id => userQueueIds.includes(id));
        setSelectedQueueIds(validQueueIds);
      }
      if (saved.selectedWhatsappIds !== undefined) {
        setSelectedWhatsappIds(normalizeIdList(saved.selectedWhatsappIds));
      }
      if (Array.isArray(saved.selectedTags)) {
        setSelectedTags(saved.selectedTags);
      }
      if (Array.isArray(saved.selectedContactTags)) {
        setSelectedContactTags(normalizeIdList(saved.selectedContactTags));
      }
      if (Array.isArray(saved.selectedUsers)) {
        setSelectedUsers(saved.selectedUsers);
      }
      if (typeof saved.sortOrder === "string") {
        setSortOrder(saved.sortOrder);
      }
      if (typeof saved.tab === "string") {
        setTab(saved.tab);
      }
      if (typeof saved.tabOpen === "string") {
        setTabOpen(saved.tabOpen);
      }
      // `searchParam` NAO e restaurado de proposito: reabrir o CRM com uma busca
      // antiga ja aplicada fazia a lista parecer quebrada/vazia sem motivo.
      if (saved.searchBy === "all" || saved.searchBy === "contact") {
        setSearchBy(saved.searchBy);
      }
    } catch (err) {
      console.error("Erro ao carregar filtros de tickets:", err);
    }
  }, [filtersStorageKey, userQueues]);

  useEffect(() => {
    if (!canShowAllFilter && showAllTickets) {
      setShowAllTickets(false);
    }
  }, [canShowAllFilter, showAllTickets]);

  useEffect(() => {
    if (!canShowWhatsappFilter && selectedWhatsappIds.length > 0) {
      setSelectedWhatsappIds([]);
    }
  }, [canShowWhatsappFilter, selectedWhatsappIds]);

  // Valida o filtro de conexões persistido (localStorage) contra as conexões
  // que o usuário pode filtrar: um ID órfão (conexão deletada/recriada) intersecta
  // vazio no backend e zera as listas silenciosamente, e um ID de conexão que ele
  // deixou de controlar não pode continuar aplicado sem aparecer no select.
  useEffect(() => {
    if (!Array.isArray(whatsApps) || whatsApps.length === 0) return;
    if (selectedWhatsappIds.length === 0) return;
    // `user` ainda sem `whatsapps`: não limpar a seleção antes de saber.
    if (getManagedWhatsappIds(user) === null) return;

    const validIds = new Set(
      filterWhatsApps.map((w) => Number(w.id))
    );
    const filtered = selectedWhatsappIds.filter((id) => validIds.has(Number(id)));

    if (filtered.length !== selectedWhatsappIds.length) {
      setSelectedWhatsappIds(filtered);
    }
  }, [whatsApps, filterWhatsApps, selectedWhatsappIds, user]);

  // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: manter "Todos" desmarcado por padrao
  // quando nao houver valor salvo no localStorage; se houver salvo (true/false), respeitar.

  // Removido: não é mais necessário buscar todas as filas
  // O filtro deve mostrar apenas as filas que o usuário tem acesso (user.queues)

  // Validar e limpar filas selecionadas quando as filas do usuário mudarem.
  // `selectedQueueIds` está nas dependências, então NUNCA se pode chamar o setter
  // com um array recém-criado sem antes comparar o conteúdo: a referência nova
  // conta como mudança de estado, o efeito roda de novo e a aba trava ("Página
  // sem resposta"). Era o que acontecia com perfil não-admin sem o menu
  // "tickets_filter_queues" marcado, onde o ramo abaixo setava a cada render.
  useEffect(() => {
    const userQueueIds = userQueues.map((q) => q.id);
    const validSelectedQueueIds = selectedQueueIds.filter(id => userQueueIds.includes(id));
    const sameIds = (a, b) =>
      a.length === b.length && a.every((id, index) => id === b[index]);

    if (!canShowQueueFilter) {
      if (!sameIds(selectedQueueIds, userQueueIds)) {
        setSelectedQueueIds(userQueueIds);
      }
      return;
    }

    if (selectedQueueIds.length === 0 && userQueueIds.length > 0) {
      setSelectedQueueIds(userQueueIds);
      return;
    }

    // Se alguma fila foi removida, atualizar o estado
    if (!sameIds(validSelectedQueueIds, selectedQueueIds)) {
      setSelectedQueueIds(validSelectedQueueIds);
    }
  }, [userQueues, selectedQueueIds, canShowQueueFilter]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
  }, [tab]);

  useEffect(() => {
    const filtersToSave = {
      showAllTickets,
      selectedQueueIds,
      selectedWhatsappIds,
      selectedTags,
      selectedContactTags,
      selectedUsers,
      sortOrder,
      tab,
      tabOpen,
      searchBy,
    };

    try {
      localStorage.setItem(filtersStorageKey, JSON.stringify(filtersToSave));
    } catch (err) {
      console.error("Erro ao salvar filtros de tickets:", err);
    }
  }, [
    filtersStorageKey,
    showAllTickets,
    selectedQueueIds,
    selectedWhatsappIds,
    selectedTags,
    selectedContactTags,
    selectedUsers,
    sortOrder,
    tab,
    tabOpen,
    searchBy,
  ]);

  // Ref (e nao `let`): uma variavel local era recriada a cada render, entao o
  // clearTimeout nunca cancelava o timer pendente e resultados de termos
  // intermediarios ainda chegavam depois do termo final.
  const searchTimeoutRef = useRef();

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeoutRef.current);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClearSearch = () => {
    clearTimeout(searchTimeoutRef.current);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      searchInputRef.current.focus();
    }
    setSearchParam("");
  };

  useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  const hasEmailConnection = Array.isArray(whatsApps)
    && whatsApps.some((w) => w?.type === "email");

  const finishEmailSync = useCallback(() => {
    clearTimeout(emailSyncWatchdogRef.current);
    emailSyncWatchdogRef.current = null;
    setSyncingEmail(false);
  }, []);

  // O backend responde 202 e sincroniza em segundo plano; o resultado chega
  // por este evento, na sala do usuário.
  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId) return undefined;

    const socket = socketManager.getSocket(companyId);

    const handleEmailSync = (data) => {
      // O evento vai para todas as abas do usuário; só a que pediu mostra o aviso.
      if (!emailSyncWatchdogRef.current) return;
      finishEmailSync();

      if (data?.action === "error") {
        toast.error(i18n.t("ticketsManager.emailSync.error"));
        return;
      }

      const processed = data?.processed || 0;
      if (processed > 0) {
        toast.success(
          i18n.t("ticketsManager.emailSync.received", { count: processed })
        );
      } else {
        toast.info(i18n.t("ticketsManager.emailSync.none"));
      }
    };

    socket.on(`company-${companyId}-emailSync`, handleEmailSync);

    return () => {
      socket.off(`company-${companyId}-emailSync`, handleEmailSync);
      socket.disconnect();
    };
  }, [user?.companyId, socketManager, finishEmailSync]);

  useEffect(() => () => clearTimeout(emailSyncWatchdogRef.current), []);

  const handleSyncEmail = async () => {
    if (syncingEmail) return;
    setSyncingEmail(true);
    // Armado ANTES do POST: o evento do socket pode chegar antes da resposta
    // HTTP. Se o evento não vier (socket caído), libera o botão; a rodada
    // continua no servidor e os chamados novos entram na lista normalmente.
    emailSyncWatchdogRef.current = setTimeout(finishEmailSync, EMAIL_SYNC_WATCHDOG_MS);
    try {
      await api.post("/whatsapp/email/sync");
    } catch (err) {
      finishEmailSync();
      toastError(err);
    }
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  // useCallback: o TagsFilter usa o handler num efeito (limpeza de tag apagada).
  const handleSelectedContactTags = useCallback((selecteds) => {
    setSelectedContactTags(selecteds.map((t) => t.id));
  }, []);

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const handleSortMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
    setSortKey(prev => prev + 1); // Força refresh mesmo se clicar na mesma opção
    handleSortMenuClose();
  };

  const handleAutoReorderChange = (event) => {
    const newValue = event.target.checked;
    setAutoReorder(newValue);
    localStorage.setItem("ticketsAutoReorder", newValue.toString());
  };

  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  // Verificar se há filtros ativos
  const hasActiveFilters = 
    (canShowQueueFilter && selectedQueueIds.length !== userQueueIds.length) ||
    (canShowWhatsappFilter && selectedWhatsappIds.length > 0) ||
    selectedTags.length > 0 ||
    selectedContactTags.length > 0 ||
    selectedUsers.length > 0;

  const handleAlarmSummary = (statusKey) => (summary) => {
    if (!summary) {
      return;
    }

    setAlarmEnabled(Boolean(summary.enabled));

    if (statusKey === "open") {
      setOpenAlarmCount(Number(summary.total) || 0);
    }

    if (statusKey === "pending") {
      setPendingAlarmCount(Number(summary.total) || 0);
    }
  };

  const isSearching = searchParam.trim() !== "";

  // Badge das abas durante a busca: só a aba ativa é consultada, então mostrar
  // um número na inativa seria enganoso — ela fica sem badge.
  const badgeContentFor = (statusKey, normalCount) => {
    if (!isSearching) return normalCount;
    return tabOpen === statusKey ? searchResultCount : null;
  };

  const openBadgeContent = badgeContentFor("open", openCount);
  const pendingBadgeContent = badgeContentFor("pending", pendingCount);

  const renderSearchResults = (status, showAll) => (
    <TicketsSearchResults
      searchParam={searchParam}
      searchBy={searchBy}
      status={status}
      showAll={showAll}
      selectedQueueIds={selectedQueueIds}
      selectedWhatsappIds={selectedWhatsappIds}
      selectedContactTagIds={selectedContactTags}
      onSelectContact={onSelectContact}
      onResultCount={setSearchResultCount}
    />
  );

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="icon label tabs example"
        >
          <Tab
            value={"open"}
            icon={<MoveToInboxIcon />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"closed"}
            icon={<CheckBoxIcon />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"search"}
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>
      {/* Barra de pesquisa e filtros unificada */}
      <Paper square elevation={0} className={classes.unifiedSearchWrapper}>
        {hasEmailConnection && (
          <Tooltip title={i18n.t("ticketsManager.emailSync.tooltip")}>
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={handleSyncEmail}
                disabled={syncingEmail}
              >
                {syncingEmail
                  ? <CircularProgress size={20} color="inherit" />
                  : <EmailIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <div className={classes.searchInputContainer}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={i18n.t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
            onKeyDown={(e) => {
              // ESC na busca limpa a busca (como no WhatsApp Web) em vez de
              // deixar o Ticket fechar a conversa aberta ao lado.
              if (e.key !== "Escape") return;
              if (!e.target.value) return;
              handleClearSearch();
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          {isSearching && (
            <IconButton size="small" onClick={handleClearSearch}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setNewTicketModalOpen(true)}
          style={{ display: "none" }}
        >
          {i18n.t("ticketsManager.buttons.newTicket")}
        </Button>

        <Tooltip title={i18n.t("ticketsList.filters.title")}>
          <IconButton
            onClick={handleFilterMenuOpen}
            size="small"
            style={{ position: "relative" }}
          >
            <FilterListIcon />
            {hasActiveFilters && <span className={classes.activeFilterBadge} />}
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box className={classes.filterPopover}>
            {canShowAllFilter && (
              <Box className={classes.filterSection}>
                <FormControlLabel
                  className={classes.filterSwitchLabel}
                  label={i18n.t("tickets.buttons.showAll")}
                  control={
                    <Switch
                      size="small"
                      checked={showAllTickets}
                      onChange={() =>
                        setShowAllTickets((prevState) => !prevState)
                      }
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              </Box>
            )}
            
            {canShowQueueFilter && (
              <Box className={classes.filterSection}>
                <Typography className={classes.filterLabel}>
                  {i18n.t("ticketsList.filters.queues")}
                </Typography>
                <TicketsQueueSelect
                  selectedQueueIds={selectedQueueIds}
                  userQueues={user?.queues}
                  onChange={(values) => setSelectedQueueIds(values)}
                />
              </Box>
            )}

            {canShowWhatsappFilter && filterWhatsApps.length > 0 && (
              <Box className={classes.filterSection}>
                <Typography className={classes.filterLabel}>
                  {i18n.t("ticketsList.filters.whatsapp")}
                </Typography>
                <TicketsWhatsappSelect
                  selectedWhatsappIds={selectedWhatsappIds}
                  whatsapps={filterWhatsApps}
                  onChange={(values) => setSelectedWhatsappIds(values)}
                />
              </Box>
            )}

            <Box className={classes.filterSection}>
              <Typography className={classes.filterLabel}>
                {i18n.t("ticketsList.filters.contactTags")}
              </Typography>
              <TagsFilter
                compact
                selectedIds={selectedContactTags}
                placeholder={i18n.t("ticketsList.filters.contactTagsPlaceholder")}
                onFiltered={handleSelectedContactTags}
              />
            </Box>

            <Box className={classes.filterSection}>
              <Typography className={classes.filterLabel}>
                {i18n.t("ticketsList.filters.searchMode")}
              </Typography>
              <FormControlLabel
                className={classes.filterSwitchLabel}
                control={
                  <Switch
                    size="small"
                    color="primary"
                    checked={searchBy === "contact"}
                    onChange={(event) => setSearchBy(event.target.checked ? "contact" : "all")}
                  />
                }
                label={i18n.t("ticketsList.filters.contactNameOnly")}
              />
            </Box>

            {tab === "search" && (
              <>
                <Box className={classes.filterSection}>
                  <Typography className={classes.filterLabel}>
                    {i18n.t("ticketsList.filters.tags")}
                  </Typography>
                  <TagsFilter onFiltered={handleSelectedTags} />
                </Box>
                {profile === "admin" && (
                  <Box className={classes.filterSection}>
                    <Typography className={classes.filterLabel}>
                      {i18n.t("ticketsList.filters.users")}
                    </Typography>
                    <UsersFilter onFiltered={handleSelectedUsers} />
                  </Box>
                )}
              </>
            )}

            <Divider style={{ margin: "16px 0" }} />
            
            <Box className={classes.filterSection}>
              <Typography className={classes.filterLabel}>
                {i18n.t("ticketsManager.sorting.title")}
              </Typography>
              <MenuItem 
                onClick={() => handleSortChange("desc")}
                selected={sortOrder === "desc"}
                className={classes.sortOption}
              >
                {i18n.t("ticketsList.sorting.newestFirst")}
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange("asc")}
                selected={sortOrder === "asc"}
                className={classes.sortOption}
              >
                {i18n.t("ticketsList.sorting.oldestFirst")}
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange("waitTimeDesc")}
                selected={sortOrder === "waitTimeDesc"}
                className={classes.sortOption}
              >
                {i18n.t("ticketsList.sorting.longestWaitFirst")}
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange("waitTimeAsc")}
                selected={sortOrder === "waitTimeAsc"}
                className={classes.sortOption}
              >
                {i18n.t("ticketsList.sorting.shortestWaitFirst")}
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange("unreadFirst")}
                selected={sortOrder === "unreadFirst"}
                className={classes.sortOption}
              >
                {i18n.t("ticketsList.sorting.unreadFirst")}
              </MenuItem>
              <Box mt={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoReorder}
                      onChange={handleAutoReorderChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={i18n.t("ticketsList.sorting.autoReorder")}
                  className={classes.filterCheckboxLabel}
                />
              </Box>
            </Box>
          </Box>
        </Popover>
      </Paper>
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Paper square elevation={0} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            className={classes.compactTabs}
            style={{ flex: 1 }}
          >
            <Tab
              label={
                <Badge
                  className={clsx(classes.badge, {
                    [classes.badgeSpaced]: Boolean(openBadgeContent),
                  })}
                  badgeContent={openBadgeContent}
                  color="primary"
                >
                  {i18n.t("ticketsList.assignedHeader")}
                </Badge>
              }
              value={"open"}
              className={clsx({
                [classes.tabAlarm]: alarmEnabled && openAlarmCount > 0,
                [classes.tabAlarmSelected]: alarmEnabled && openAlarmCount > 0 && tabOpen === "open",
              })}
              style={{ flex: 1, maxWidth: "none", minWidth: 0 }}
            />
            <Tab
              label={
                <Badge
                  className={clsx(classes.badge, {
                    [classes.badgeSpaced]: Boolean(pendingBadgeContent),
                  })}
                  badgeContent={pendingBadgeContent}
                  color="secondary"
                >
                  {i18n.t("ticketsList.pendingHeader")}
                </Badge>
              }
              value={"pending"}
              className={clsx({
                [classes.tabAlarm]: alarmEnabled && pendingAlarmCount > 0,
                [classes.tabAlarmSelected]: alarmEnabled && pendingAlarmCount > 0 && tabOpen === "pending",
              })}
              style={{ flex: 1, maxWidth: "none", minWidth: 0 }}
            />
            <Tab
              label={i18n.t("ticketsList.contactsHeader")}
              value={"contacts"}
              icon={<ContactsIcon fontSize="small" />}
              classes={{
                wrapper: classes.iconLabelWrapper
              }}
              style={{ flex: 1, maxWidth: "none", minWidth: 0 }}
            />
          </Tabs>
          <IconButton
            onClick={handleSortMenuOpen}
            size="small"
            style={{ marginRight: 8, display: "none" }}
          >
            <SortIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleSortMenuClose}
            style={{ display: "none" }}
          >
            <MenuItem 
              onClick={() => handleSortChange("desc")}
              selected={sortOrder === "desc"}
            >
              {i18n.t("ticketsList.sorting.newestFirst")}
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange("asc")}
              selected={sortOrder === "asc"}
            >
              {i18n.t("ticketsList.sorting.oldestFirst")}
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange("waitTimeDesc")}
              selected={sortOrder === "waitTimeDesc"}
            >
              {i18n.t("ticketsList.sorting.longestWaitFirst")}
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange("waitTimeAsc")}
              selected={sortOrder === "waitTimeAsc"}
            >
              {i18n.t("ticketsList.sorting.shortestWaitFirst")}
            </MenuItem>
            <Divider />
            <MenuItem>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoReorder}
                    onChange={handleAutoReorderChange}
                    color="primary"
                    size="small"
                  />
                }
                label={i18n.t("ticketsList.sorting.autoReorder")}
                style={{ margin: 0 }}
              />
            </MenuItem>
          </Menu>
        </Paper>
        <Paper className={classes.ticketsWrapper}>
          {isSearching ? (
            // Com termo digitado, a aba exibe os resultados agrupados por motivo
            // (Contatos / Grupos em comum / Mensagens), no padrao WhatsApp. A
            // aba selecionada continua definindo o escopo da busca.
            renderSearchResults(
              // aba Contatos não tem status: busca em todos os contatos
              tabOpen === "contacts" ? null : tabOpen,
              tabOpen === "contacts" ? true : tabOpen === "open" && showAllTickets
            )
          ) : (
            <>
              <Box style={{ display: tabOpen === "open" ? "block" : "none", height: "100%" }}>
                <TicketsList
                  status="open"
                  showAll={showAllTickets}
                  selectedQueueIds={selectedQueueIds}
                  selectedWhatsappIds={selectedWhatsappIds}
                  contactTags={selectedContactTags}
                  updateCount={(val) => setOpenCount(val)}
                  updateAlarmSummary={handleAlarmSummary("open")}
                  style={applyPanelStyle("open")}
                  sortOrder={sortOrder}
                  sortKey={sortKey}
                  autoReorder={autoReorder}
                />
              </Box>
              <Box style={{ display: tabOpen === "pending" ? "block" : "none", height: "100%" }}>
                <TicketsList
                  status="pending"
                  selectedQueueIds={selectedQueueIds}
                  selectedWhatsappIds={selectedWhatsappIds}
                  contactTags={selectedContactTags}
                  updateCount={(val) => setPendingCount(val)}
                  updateAlarmSummary={handleAlarmSummary("pending")}
                  style={applyPanelStyle("pending")}
                  sortOrder={sortOrder}
                  sortKey={sortKey}
                  autoReorder={autoReorder}
                />
              </Box>
              {tabOpen === "contacts" && (
                <ContactsListPanel
                  style={applyPanelStyle("contacts")}
                  tagIds={selectedContactTags}
                  onSelectContact={onSelectContact}
                  active={tab === "open" && tabOpen === "contacts"}
                />
              )}
            </>
          )}
        </Paper>
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        {isSearching ? (
          renderSearchResults("closed", true)
        ) : (
          <TicketsList
            status="closed"
            showAll={true}
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            contactTags={selectedContactTags}
            sortOrder={sortOrder}
            sortKey={sortKey}
            autoReorder={autoReorder}
          />
        )}
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          tags={selectedTags}
          users={selectedUsers}
          selectedQueueIds={selectedQueueIds}
          selectedWhatsappIds={selectedWhatsappIds}
          sortOrder={sortOrder}
          sortKey={sortKey}
          autoReorder={autoReorder}
          searchBy={searchBy}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManagerTabs;
